import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Loader from '@/components/Loader';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import useRouterQuery from '@/utils/routerQuery';
import {
  beginSession,
  clearUserData,
  loadUserProfile,
  selectIsAppInitialised,
  selectSessionLoading,
  selectSessionUser,
  selectUserRejected,
  setIsAuthenticated,
  signIn,
  type User,
} from './sessionSlice';
import {
  addConfig,
  type ApplicationConfig,
  selectGlobalError,
  setGlobalError,
} from '@/app/coreSlice';
import appLocalStorage from './appLocalStorage';
import routes from '@/app/routes';

const withSession =
  (WrappedComponent: React.ComponentType<any>) => (props: any) => {
    const query = useRouterQuery();
    const navigate = useNavigate();

    const appInitialised: boolean = useAppSelector(selectIsAppInitialised);
    const loading: boolean = useAppSelector(selectSessionLoading);
    const user: User | null = useAppSelector(selectSessionUser);
    const userRejected: boolean = useAppSelector(selectUserRejected);
    const globalError = useAppSelector(selectGlobalError);
    const dispatch = useAppDispatch();

    useEffect(() => {
      // Config should be passed as queryparams from the Portal
      const config: ApplicationConfig = {
        region: query.get('region') || '',
        // Example of production unit Sluisklil AmmoniaE
        productionUnit: query.get('site') || 'YARA.SLU.AmmoniaE',
        classification: query.get('classification') || '',
      };

      if (config.productionUnit) {
        dispatch(beginSession(query.get('code')))
          .unwrap()
          .then(() => {
            dispatch(setIsAuthenticated(true));
            // dispatch(
            //   loadUserProfile({ 
            //     token: appLocalStorage.getAuthToken() || '',
            //     app: 'elc', // Switch to the app you are making.
            //     resource: config.productionUnit,
            //     stage: import.meta.env.VITE_APP_ENV || '',
            //   })
            // );
            appLocalStorage.setProductionUnit(config.productionUnit);
            dispatch(addConfig(config));
          });
      }
    }, []);

    useEffect(() => {
      // if user is rejected or doen't have any role applied we set a global error to protect the content.
      if (user && userRejected) {
        dispatch(
          setGlobalError({
            message: 'There was a problem validating your user credentials',
          })
        );
        const site = appLocalStorage.getProductionUnit();
        dispatch(clearUserData());
        if (site) {
          appLocalStorage.setProductionUnit(site);
        }
      }
    }, [userRejected, user]);

    useEffect(() => {
      // When error is set we navigate to the error page.
      if (globalError) {
        navigate(routes.ERROR);
      }
    }, [globalError]);

    const signOutHandler = () => {
      const site = appLocalStorage.getProductionUnit();
      dispatch(clearUserData());
      navigate({
        pathname: routes.LOGIN,
      });
      window.location.reload();
    };

    const signInHandler = () => {
      dispatch(signIn());
    };

    if (!appInitialised || loading) {
      return <Loader />;
    }
    return (
      <WrappedComponent
        user={user}
        signIn={signInHandler}
        signOut={signOutHandler}
        {...props}
      />
    );
  };

export default withSession;
